import { darkColors } from '@tryhackme/thm-ui-components';
import type { TooltipProps } from '@tryhackme/thm-ui-components/dist/components/atoms/tooltip/tooltip.types';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setHelperTooltipElements } from 'src/app/reducers/room/room.reducer';
import { calculatePosition } from './helper';
import { HelperTooltip } from './helper-tooltip';
import { useTutorialCompleteMutation } from './helper-tooltip.slice';
import { StyledHelperTooltipWrapper, StyledTooltipOverlay } from './helper-tooltip.styles';
export const HelperTooltipWrapper = () => {
  const helperTooltipElements = useAppSelector(state => state.room.helperTooltipElements);
  const dispatch = useAppDispatch();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [pointer, setPointer] = useState<number>(-1);
  const [previousNodeZIndex, setPreviousNodeZIndex] = useState<string>('-1');
  const [previousParentZIndex, setPreviousParentZIndex] = useState<string>('-1');
  const [previousPosition, setPreviousPosition] = useState('block');
  const [tutorialCompleteTrigger] = useTutorialCompleteMutation();
  const [positionInfo, setPositionInfo] = useState<{
    data: {
      top?: number;
      left?: number;
      bottom?: number;
      right?: number;
    };
    arrowPosition: TooltipProps['arrowPosition'];
    text: string;
  } | null>(null);
  const tutorialComplete = useCallback(async () => {
    if (helperTooltipElements?.userTutorialProperty) await tutorialCompleteTrigger({
      completed: true,
      tutorial: helperTooltipElements?.userTutorialProperty
    });
  }, [helperTooltipElements, tutorialCompleteTrigger]);
  const handleNextAndBackStep = useCallback((back?: boolean) => {
    try {
      // reset the previous node
      const oldNodeData = helperTooltipElements?.elements[pointer];
      if (oldNodeData) {
        const oldElement = document.querySelector(oldNodeData.id) as HTMLDivElement;
        if (oldElement) {
          oldElement.style.border = 'none';
          oldElement.style.zIndex = previousNodeZIndex;
          oldElement.style.position = previousPosition;
          if (oldNodeData.parentId) {
            const parentElement = document.querySelector(oldNodeData.parentId) as HTMLDivElement;
            if (parentElement) {
              parentElement.style.zIndex = previousParentZIndex;
            }
          }
        }
      } else {
        setShowTooltip(true);
      }
      const newNodeData = helperTooltipElements?.elements[pointer + (back ? -1 : 1)];
      if (newNodeData) {
        let element = document.querySelector(newNodeData.id) as HTMLElement;
        if (element) {
          setPreviousNodeZIndex(element.style.zIndex);
          setPreviousPosition(element.style.position);
          element.style.border = `2px solid ${darkColors.info.secondary}`;
          element.style.zIndex = '101';
          element.style.position = 'relative';

          // First scroll the element into view
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Wait for scroll animation to complete before calculating position
          setTimeout(() => {
            if (newNodeData.parentId) {
              const parentElement = document.querySelector(newNodeData.parentId) as HTMLDivElement;
              if (parentElement) {
                setPreviousParentZIndex(parentElement.style.zIndex);
                parentElement.style.zIndex = '4';
              }
            }
            setPositionInfo({
              data: {
                ...(newNodeData.position.left && {
                  left: calculatePosition(element, newNodeData.position.left)
                }),
                ...(newNodeData.position.top && {
                  top: calculatePosition(element, newNodeData.position.top)
                }),
                ...(newNodeData.position.right && {
                  right: calculatePosition(element, newNodeData.position.right)
                }),
                ...(newNodeData.position.bottom && {
                  bottom: calculatePosition(element, newNodeData.position.bottom)
                })
              },
              arrowPosition: newNodeData.arrowPosition,
              text: newNodeData.text
            });
            setPointer(prev => prev + (back ? -1 : 1));
          }, 650);
        }
        // By default, if the element is not found, we will try to find the body element and display the tooltip
        else {
          element = document.querySelector('body') as HTMLBodyElement;
          setPreviousNodeZIndex(element.style.zIndex);
          setPreviousPosition(element.style.position);
          element.style.zIndex = '101';
          element.style.position = 'relative';

          // First scroll the element into view
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Wait for scroll animation to complete before setting position
          setTimeout(() => {
            setPositionInfo({
              data: {
                left: 1,
                top: 100
              },
              arrowPosition: newNodeData.arrowPosition,
              text: newNodeData.text
            });
            setPointer(prev => prev + (back ? -1 : 1));
          }, 650);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        tutorialComplete();
        setShowTooltip(false);
        setPointer(-1);
        dispatch(setHelperTooltipElements());
        document.body.style.removeProperty('position');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [dispatch, helperTooltipElements?.elements, pointer, previousNodeZIndex, previousParentZIndex, previousPosition, tutorialComplete]);
  useEffect(() => {
    if (helperTooltipElements?.elements && helperTooltipElements?.elements.length > 0 && !showTooltip) {
      document.body.style.position = 'relative';
      setShowTooltip(true);
      handleNextAndBackStep();
    }
  }, [handleNextAndBackStep, showTooltip, helperTooltipElements?.elements]);
  return <div data-sentry-component="HelperTooltipWrapper" data-sentry-source-file="helper-tooltip-wrapper.tsx">
      {showTooltip && helperTooltipElements?.elements && createPortal(<StyledHelperTooltipWrapper arrowPosition={positionInfo?.arrowPosition}>
            <StyledTooltipOverlay />

            <HelperTooltip currentStep={pointer + 1} maxSteps={helperTooltipElements.elements.length}
      // eslint-disable-next-line react/jsx-no-bind
      onNextClick={handleNextAndBackStep.bind(null, false)} text={positionInfo?.text ?? ''}
      // eslint-disable-next-line react/jsx-no-bind
      onBackClick={handleNextAndBackStep.bind(null, true)} {...positionInfo?.data} arrowPosition={positionInfo?.arrowPosition ?? 'top-center'} />
          </StyledHelperTooltipWrapper>, document.body)}
    </div>;
};